import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChartDataService } from "../services/ChartDataService";
import { useUnifiedAuth } from "../auth/useUnifiedAuth";
import { useSAPDataStore } from "../store/SAPDataStore";
import { shiftStyles, cx } from "../styles";

interface AvailableShift {
  shift: string;
  description?: string;
  machineCount?: number;
}

type ShiftSelectorProps = {
  shifts: string[];
  setShifts: (shifts: string[]) => void;
  machineNumber?: string; // Machine number (maschinennummer) - sent as 'serialno' to backend
  lineId?: string; // Line ID (linieBK) - sent as 'line_id' to backend for line-level data
  className?: string;
  onAvailabilityChange?: (
    hasAvailableShifts: boolean,
    isLoading: boolean,
  ) => void;
};

const ShiftSelector: React.FC<ShiftSelectorProps> = ({
  shifts,
  setShifts,
  machineNumber,
  lineId,
  className = "",
  onAvailabilityChange,
}) => {
  const { t } = useTranslation();
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();

  const [availableShifts, setAvailableShifts] = useState<AvailableShift[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Whether a token is currently available. We depend on this boolean (not the
  // token string) so the fetch runs once when auth first resolves
  // (absent -> present), but later token ROTATIONS (string -> new string, both
  // truthy) don't re-trigger it and cause repeated chart refetches.
  const hasToken = !!token;

  // Keep the latest token / translation fn in refs so they don't become fetch
  // dependencies. Keycloak rotates `token` periodically (and the Live Shift
  // worker can trigger a refresh), which would otherwise change the fetch
  // callback identity, re-run the effect, and re-select shifts — cascading
  // multiple chart refetches. The query only truly depends on machine/line and
  // the date range.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const tRef = useRef(t);
  tRef.current = t;
  // setShifts / onAvailabilityChange can also change identity across renders;
  // hold them in refs so the fetch callback stays stable.
  const setShiftsRef = useRef(setShifts);
  setShiftsRef.current = setShifts;

  // Fetch available shifts from NATS backend
  const fetchAvailableShifts = useCallback(async () => {
    const currentToken = tokenRef.current;
    const translate = tRef.current;
    const applyShifts = setShiftsRef.current;

    // Need either machineNumber or lineId
    if ((!machineNumber && !lineId) || !dateRange.start || !dateRange.end) {
      setAvailableShifts([]);
      applyShifts([]);
      return;
    }

    if (!currentToken) {
      setAvailableShifts([]);
      applyShifts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use different request based on whether it's machine or line level
      let response;
      if (lineId) {
        // For lines, use line_bk parameter with line-specific endpoint
        response = await ChartDataService.fetchLineAvailableShifts(
          {
            line_bk: lineId,
            date_from: dateRange.start,
            date_to: dateRange.end,
          },
          currentToken,
        );
      } else {
        // For machines, use serialno parameter
        response = await ChartDataService.fetchAvailableShifts(
          {
            serialno: machineNumber,
            date_from: dateRange.start,
            date_to: dateRange.end,
          },
          currentToken,
        );
      }

      // Response format: { success: true, data: [{shift_name, machine_sn, exist_SAP}, ...] }
      if (response.success) {
        // Handle missing data as empty array
        const shifts = Array.isArray(response.data) ? response.data : [];

        if (shifts.length === 0) {
          setAvailableShifts([]);
          applyShifts([]);
        } else {
          // Extract shift names from flat response
          const shiftNames = shifts.map(
            (item: { shift_name: string }) => item.shift_name,
          );

          // Process available shifts
          const availableShiftList = shiftNames.map((shift: string) => ({
            shift: shift,
            description: `${translate("shift")} ${shift}`,
            machineCount: undefined,
          }));
          setAvailableShifts(availableShiftList);

          // Auto-select all shifts by default
          applyShifts(shiftNames);
        }
      } else {
        console.error("ShiftSelector: Request failed:", response.error);
        setError(response.error || "Failed to fetch shifts");
        setAvailableShifts([]);
        applyShifts([]);
      }
    } catch (error) {
      console.error("ShiftSelector: Failed to fetch available shifts:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch shifts",
      );
      setAvailableShifts([]);
      applyShifts([]);
    } finally {
      setLoading(false);
    }
  }, [machineNumber, lineId, dateRange.start, dateRange.end, hasToken]);

  // Fetch shifts when dependencies change
  useEffect(() => {
    fetchAvailableShifts();
  }, [fetchAvailableShifts]);

  // Notify parent of availability changes
  useEffect(() => {
    onAvailabilityChange?.(availableShifts.length > 0, loading);
  }, [availableShifts.length, loading, onAvailabilityChange]);

  // Handle shift selection
  const handleShiftToggle = (shift: string) => {
    const isSelected = shifts.includes(shift);
    if (isSelected) {
      // Remove shift
      setShifts(shifts.filter((s) => s !== shift));
    } else {
      // Add shift
      setShifts([...shifts, shift]);
    }
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (shifts.length === availableShifts.length) {
      // All selected, clear selection
      setShifts([]);
    } else {
      // Select all
      setShifts(availableShifts.map((s) => s.shift));
    }
  };

  if (loading) {
    return (
      <div className={cx(shiftStyles.loading.container, className)}>
        <div className={shiftStyles.loading.spinner}></div>
        <span className={shiftStyles.loading.text}>
          {t("loadingShifts") || "Loading shifts..."}
        </span>
      </div>
    );
  }

  const allSelected = shifts.length === availableShifts.length;
  const someSelected =
    shifts.length > 0 && shifts.length < availableShifts.length;

  // Handle case when no shifts are available
  if (availableShifts.length === 0 && !loading) {
    const noMachineOrLineSelected = !machineNumber && !lineId;
    const noDateRange = !dateRange.start || !dateRange.end;

    let message = t("noShiftsAvailable") || "No shifts available";

    if (noMachineOrLineSelected) {
      message = t("selectMachineFirst") || "Select a machine first";
    } else if (noDateRange) {
      message = t("selectDateRangeFirst") || "Select a date range first";
    }

    return (
      <div className={cx(shiftStyles.container, className)}>
        <div className={shiftStyles.noShifts.message}>
          <span className={shiftStyles.noShifts.text}>{message}</span>
        </div>

        {error && (
          <div className={shiftStyles.error.container}>
            {t("shiftLoadError") || "Could not load shifts"}: {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cx(shiftStyles.container, className)}>
      <div className={shiftStyles.buttonGroup.container}>
        {/* Select All button */}
        <button
          className={cx(
            shiftStyles.buttonGroup.selectAll.base,
            allSelected
              ? shiftStyles.buttonGroup.selectAll.allSelected
              : someSelected
                ? shiftStyles.buttonGroup.selectAll.someSelected
                : shiftStyles.buttonGroup.selectAll.noneSelected,
          )}
          onClick={handleSelectAll}
          title={
            allSelected
              ? t("deselectAll") || "Deselect All"
              : t("selectAll") || "Select All"
          }
        >
          {allSelected ? "✓" : someSelected ? "◐" : "○"}
        </button>

        {/* Individual shift buttons */}
        {availableShifts.map((shiftInfo) => {
          const isSelected = shifts.includes(shiftInfo.shift);
          return (
            <button
              key={shiftInfo.shift}
              className={cx(
                shiftStyles.buttonGroup.shift.base,
                isSelected
                  ? shiftStyles.buttonGroup.shift.selected
                  : shiftStyles.buttonGroup.shift.unselected,
              )}
              onClick={() => handleShiftToggle(shiftInfo.shift)}
              title={shiftInfo.description}
            >
              {shiftInfo.shift}
            </button>
          );
        })}
      </div>

      {error && (
        <div className={shiftStyles.error.container}>
          {t("shiftLoadError") || "Could not load shifts"}: {error}
        </div>
      )}
    </div>
  );
};

export default ShiftSelector;
